import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface AboveTooltipProps {
  /** Tooltip text. */
  content: ReactNode;
  children: ReactNode;
  /** Max width of the bubble in px. */
  maxWidth?: number;
}

const GAP = 8;
const EDGE = 12;

/**
 * Tooltip shown above its anchor (e.g. a table header), so it never covers the
 * rows below. Portalled to <body> so table overflow cannot clip it; shown on
 * hover and keyboard focus, linked with aria-describedby.
 */
export default function AboveTooltip({ content, children, maxWidth = 280 }: AboveTooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; arrowLeft: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    const a = anchor.getBoundingClientRect();
    const width = bubble.offsetWidth;
    const centre = a.left + a.width / 2;
    const left = Math.min(Math.max(centre - width / 2, EDGE), window.innerWidth - EDGE - width);
    setPos({ left, top: a.top - GAP, arrowLeft: centre - left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const show = () => setOpen(true);
  const hide = () => {
    setOpen(false);
    setPos(null);
  };

  return (
    <span
      ref={anchorRef}
      className="inline-flex max-w-full"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open && createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="tooltip"
          style={{
            position: 'fixed',
            left: pos?.left ?? -9999,
            top: pos?.top ?? -9999,
            transform: 'translateY(-100%)',
            maxWidth,
            visibility: pos ? 'visible' : 'hidden',
          }}
          className="pointer-events-none z-overlay rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal leading-snug text-gray-100 shadow-lg"
        >
          {content}
          <span
            aria-hidden
            style={{ left: (pos?.arrowLeft ?? 0) - 5 }}
            className="absolute top-full h-0 w-0 border-x-4 border-t-4 border-x-transparent border-t-gray-600"
          />
        </div>,
        document.body,
      )}
    </span>
  );
}
